import './transacao-form-modal.js';
import type { ContaComSaldo, Transacao, FaturamentoPeriodo } from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgChart } from '@pgcustom/design-system';
import type { FinanceiroApi } from './financeiro.api.js';
import type { PermissoesFinanceiro } from './financeiro.permissoes.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { ErpTransacaoFormModal } from './transacao-form-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'tipo', label: 'Tipo' },
  { key: 'valor', label: 'Valor', align: 'right' },
  { key: 'descricao', label: 'Descrição' },
  { key: 'origem', label: 'Origem' },
  { key: 'criadoEm', label: 'Data' },
];

/**
 * `<erp-financeiro-page>` — livro-razão do ERP (Sprint 5,
 * `ROADMAP.md`): contas com saldo (`pg-kpi-card`, mesmo componente do
 * Dashboard — reuso real por 2+ telas) + listagem de transações
 * (`pg-table`, mesmo padrão de `<erp-pedidos-page>`). Botão "Novo
 * lançamento" só aparece quando `permissoes.criar` — a autorização real
 * continua sempre no backend, este espelho só decide o que mostrar.
 */
export class ErpFinanceiroPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _financeiroApi: FinanceiroApi | null = null;
  private _permissoes: PermissoesFinanceiro = { ler: false, criar: false };

  private resultadoContas: Resultado<ContaComSaldo[]> = { status: 'carregando' };
  private resultadoTransacoes: Resultado<Transacao[]> = { status: 'carregando' };
  private resultadoFaturamento: Resultado<FaturamentoPeriodo[]> = { status: 'carregando' };

  private modalLancamento!: ErpTransacaoFormModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set financeiroApi(valor: FinanceiroApi) {
    this._financeiroApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesFinanceiro) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._financeiroApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-5, 24px);
          flex-wrap: wrap;
        }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0;
        }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        h2 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-md, 1.125rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0 0 var(--pg-space-4, 16px);
        }
        .contas {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-6, 32px);
        }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(260px, 320px); gap: var(--pg-space-4, 16px); align-items: start; margin-bottom: var(--pg-space-6, 32px); }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
        .atalhos { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-6, 32px); }
        @media (max-width: 720px) { .atalhos { grid-template-columns: 1fr; } }
        .atalho { text-align: left; display: flex; flex-direction: column; gap: 6px; background: var(--pg-color-bg-elevado, #171b20); border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 10px; padding: 18px; cursor: pointer; color: inherit; font-family: inherit; }
        .atalho:hover { background: var(--pg-color-bg-hover, #22262b); border-color: var(--pg-color-dourado, #c9a24b); }
        .atalho-titulo { font-family: var(--pg-font-familia-display, system-ui); font-weight: 700; font-size: var(--pg-font-size-base, 1rem); color: var(--pg-color-dourado, #c9a24b); }
        .atalho-desc { font-size: var(--pg-font-size-sm, 0.85rem); color: var(--pg-color-texto-secundario, #9aa1ab); line-height: 1.5; }
      </style>
      <div class="topo">
        <div>
          <h1>Financeiro</h1>
          <div class="subtitulo">Contas, transações e evolução financeira real</div>
        </div>
        ${this._permissoes.criar ? '<pg-button id="botao-novo" variant="primary">Novo lançamento</pg-button>' : ''}
      </div>
      <div id="contas" class="contas"></div>

      <div class="layout">
        <pg-card padding="compacto">
          <span slot="header">Evolução financeira</span>
          <div id="evolucao"><pg-empty-state variant="carregando" titulo="Evolução financeira"></pg-empty-state></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Receita x despesa</span>
          <div id="donut-tipo"><pg-empty-state variant="carregando" titulo="Receita x despesa"></pg-empty-state></div>
        </pg-card>
      </div>

      <h2>Módulos do Financeiro</h2>
      <div class="atalhos">
        <button class="atalho" data-ir="/financeiro/faturamento">
          <span class="atalho-titulo">Pedidos / Faturamento →</span>
          <span class="atalho-desc">Todos os pedidos pagos, identificação fiscal (NFPG) e KPIs de faturamento.</span>
        </button>
        <button class="atalho" data-ir="/financeiro/caixa-banco">
          <span class="atalho-titulo">Caixa Banco →</span>
          <span class="atalho-desc">Contas Stone / Mercado Pago / InfinitePay / C6, CC PG Custom, participantes, prestadores, parceiros, extratos e saques.</span>
        </button>
      </div>

      <h2>Livro-razão — transações</h2>
      <pg-card padding="compacto">
        <div id="transacoes"></div>
      </pg-card>
      <erp-transacao-form-modal id="modal-lancamento"></erp-transacao-form-modal>
    `;

    this.modalLancamento = this.root.querySelector('#modal-lancamento') as ErpTransacaoFormModal;
    if (this._financeiroApi) this.modalLancamento.financeiroApi = this._financeiroApi;
    this.modalLancamento.aoSalvar = () => void this.carregar();

    this.root.querySelector('#botao-novo')?.addEventListener('pg-click', () => {
      const contas = this.resultadoContas.status === 'ok' ? this.resultadoContas.dado : [];
      this.modalLancamento.abrirParaCriar(contas);
    });

    this.root.querySelectorAll<HTMLButtonElement>('.atalho').forEach((botao) => {
      botao.addEventListener('click', () => {
        const caminho = botao.dataset.ir;
        if (caminho) {
          this.dispatchEvent(
            new CustomEvent('erp-navegar', { bubbles: true, composed: true, detail: { caminho } })
          );
        }
      });
    });

    this.atualizarContas();
    this.atualizarTransacoes();
  }

  private async carregar(): Promise<void> {
    const api = this._financeiroApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoContas = { status: 'sem-permissao' };
      this.resultadoTransacoes = { status: 'sem-permissao' };
      this.resultadoFaturamento = { status: 'sem-permissao' };
      this.atualizarContas();
      this.atualizarTransacoes();
      this.atualizarEvolucao();
      return;
    }

    this.resultadoContas = { status: 'carregando' };
    this.resultadoTransacoes = { status: 'carregando' };
    this.resultadoFaturamento = { status: 'carregando' };
    this.atualizarContas();
    this.atualizarTransacoes();
    this.atualizarEvolucao();

    this.resultadoContas = await api.listarContas();
    this.atualizarContas();

    this.resultadoTransacoes = await api.listarTransacoes({ limit: 50 });
    this.atualizarTransacoes();

    this.resultadoFaturamento = await api.obterFaturamentoPorPeriodo(6);
    this.atualizarEvolucao();
  }

  private atualizarContas(): void {
    const container = this.root.querySelector('#contas');
    if (!container) return;

    if (this.resultadoContas.status === 'ok') {
      container.innerHTML = this.resultadoContas.dado
        .map(
          (conta) =>
            `<pg-kpi-card label="${escaparHtml(conta.nome)}" valor="${escaparHtml(formatBRL(centavosParaReais(conta.saldoCentavos)))}" estado="ok"${conta.ativa ? '' : ' dica="inativa"'}></pg-kpi-card>`
        )
        .join('');
    } else {
      const { titulo, descricao } = estadoVazioContas(this.resultadoContas);
      container.innerHTML = `<pg-kpi-card label="${escaparHtml(titulo)}" estado="${this.resultadoContas.status === 'carregando' ? 'carregando' : 'indisponivel'}" dica="${escaparHtml(descricao)}"></pg-kpi-card>`;
    }
  }

  private atualizarTransacoes(): void {
    const container = this.root.querySelector('#transacoes');
    if (!container) return;

    if (this.resultadoTransacoes.status === 'ok') {
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.resultadoTransacoes.dado.map(transacaoParaLinha);

      const donut = this.root.querySelector('#donut-tipo');
      if (donut) {
        const receitas = this.resultadoTransacoes.dado.filter((t) => t.tipo === 'receita');
        const despesas = this.resultadoTransacoes.dado.filter((t) => t.tipo === 'despesa');
        donut.innerHTML = '<pg-chart></pg-chart>';
        const chart = donut.querySelector('pg-chart') as PgChart;
        chart.type = 'donut';
        chart.series = [{ key: 'valor', label: '' }];
        chart.points = [
          { label: 'Receita', values: { valor: receitas.reduce((s, t) => s + t.valorCentavos, 0) }, color: 'var(--pg-color-sucesso)' },
          { label: 'Despesa', values: { valor: despesas.reduce((s, t) => s + t.valorCentavos, 0) }, color: 'var(--pg-color-erro)' },
        ];
        chart.formatarValor = (centavos) => formatBRL(centavosParaReais(centavos));
      }
      return;
    }

    const { variant, titulo, descricao } = estadoVazioTransacoes(this.resultadoTransacoes);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    const donut = this.root.querySelector('#donut-tipo');
    if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Receita x despesa" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarEvolucao(): void {
    const container = this.root.querySelector('#evolucao');
    if (!container) return;

    if (this.resultadoFaturamento.status === 'ok') {
      container.innerHTML = '<pg-chart></pg-chart>';
      const chart = container.querySelector('pg-chart') as PgChart;
      chart.type = 'line';
      chart.series = [
        { key: 'receita', label: 'Receita', color: 'var(--pg-color-sucesso)' },
        { key: 'despesa', label: 'Despesa', color: 'var(--pg-color-erro)' },
      ];
      chart.points = this.resultadoFaturamento.dado.map((p) => ({
        label: p.periodo,
        values: { receita: p.receitaCentavos, despesa: p.despesaCentavos },
      }));
      chart.formatarValor = (centavos) => formatBRL(centavosParaReais(centavos));
      return;
    }

    const variant =
      this.resultadoFaturamento.status === 'carregando'
        ? 'carregando'
        : this.resultadoFaturamento.status === 'sem-permissao'
          ? 'sem-permissao'
          : this.resultadoFaturamento.status === 'vazio'
            ? 'vazio'
            : 'erro';
    const descricao =
      this.resultadoFaturamento.status === 'erro'
        ? this.resultadoFaturamento.mensagem
        : this.resultadoFaturamento.status === 'sem-permissao'
          ? 'você não tem permissão para ver este dado'
          : this.resultadoFaturamento.status === 'vazio'
            ? 'nenhuma transação registrada nos últimos meses'
            : '';
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="Evolução financeira" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }
}

function transacaoParaLinha(transacao: Transacao) {
  return {
    tipo: {
      texto: transacao.tipo === 'receita' ? 'Receita' : 'Despesa',
      tom: (transacao.tipo === 'receita' ? 'sucesso' : 'erro') as 'sucesso' | 'erro',
    },
    valor: formatBRL(centavosParaReais(transacao.valorCentavos)),
    descricao: transacao.descricao,
    origem: transacao.origem,
    criadoEm: formatarDataHora(transacao.criadoEm),
  };
}

function estadoVazioContas(resultado: Resultado<ContaComSaldo[]>): { titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { titulo: 'Carregando…', descricao: '' };
    case 'sem-permissao':
      return { titulo: 'Sem acesso', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { titulo: 'Erro', descricao: resultado.mensagem };
    default:
      return { titulo: 'Nenhuma conta', descricao: 'nenhuma conta cadastrada ainda' };
  }
}

function estadoVazioTransacoes(resultado: Resultado<Transacao[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Transações', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Transações', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Transações', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhuma transação encontrada', descricao: 'lançamentos aparecem aqui conforme pedidos são pagos ou lançamentos manuais são criados' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-financeiro-page', ErpFinanceiroPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-financeiro-page': ErpFinanceiroPage;
  }
}
